import { useEffect, useCallback } from 'react';
import { cartListener } from '~/firebase/listeners/cart';

import uidAtom from '~/recoil/atom/uid';
import emailAtom from '~/recoil/atom/email';
import ordersAtom from '~/recoil/atom/orders';
import couponAtom from '~/recoil/atom/coupon';
import taxInfoAtom from '~/recoil/atom/taxInfo';
import taxTypeAtom from '~/recoil/atom/taxType';
import cartTypeAtom from '~/recoil/atom/cartType';
import orderTypeAtom from '~/recoil/atom/orderType';
import cartStatusAtom from '~/recoil/atom/cartStatus';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import campaignIdAtom from '~/recoil/atom/campaignId';
import pickMealEndAtom from '~/recoil/atom/pickMealEnd';
import paymentTypeAtom from '~/recoil/atom/paymentType';
import groupHostInfoAtom from '~/recoil/atom/groupHostInfo';
import remotePickMealPointAtom from '~/recoil/atom/remotePickMealPoint';

import globalDataStatusAtom from '~/recoil/atom/globalDataStatus';

import { useRecoilState, useSetRecoilState } from 'recoil';

import * as fp from 'lodash/fp';

import { PLATFORM_ID } from '~/globalVariables';

// types
import { SnapshotFn } from '~/firebase/listeners/type';
import { FirebaseDataStatus } from '~/types/utilities';
import { CartType, PlatformCartInFirestore } from '~/types/cart';

export default function useGroupCart(groupId: string) {
  const [globalDataStatus, setGlobalDataStatus] = useRecoilState(globalDataStatusAtom);

  const setUid = useSetRecoilState(uidAtom);
  const setEmail = useSetRecoilState(emailAtom);
  const setOrders = useSetRecoilState(ordersAtom);
  const setTaxInfo = useSetRecoilState(taxInfoAtom);
  const setTaxType = useSetRecoilState(taxTypeAtom);
  const setCoupon = useSetRecoilState(couponAtom);
  const setCartType = useSetRecoilState(cartTypeAtom);
  const setOrderType = useSetRecoilState(orderTypeAtom);
  const setCartStatus = useSetRecoilState(cartStatusAtom);
  const setPickMealAt = useSetRecoilState(pickMealAtAtom);
  const setCampaignId = useSetRecoilState(campaignIdAtom);
  const setPickMealEnd = useSetRecoilState(pickMealEndAtom);
  const setPaymentType = useSetRecoilState(paymentTypeAtom);
  const setRemotePickMealPoint = useSetRecoilState(remotePickMealPointAtom);
  const setGroupHostInfo = useSetRecoilState(groupHostInfoAtom);

  const handleSnapShot = useCallback<SnapshotFn>((doc) => {
    const data = doc.data() as PlatformCartInFirestore | undefined;

    if (!doc.exists() || !data) {
      setGlobalDataStatus(fp.set('cart', FirebaseDataStatus.NOT_EXIST));
      return;
    }
    setGlobalDataStatus(fp.set('cart', FirebaseDataStatus.EXIST));

    setUid(data.uid);
    setEmail(data.email);
    setOrders(data.orders);
    setTaxInfo(data.taxInfo);
    setTaxType(data.taxType);
    setCartType(data.cartType);
    setCartStatus(data.status);
    setOrderType(data.orderType);
    setCoupon(data.coupon || null);
    setPickMealAt(data.pickMealAt?.toDate() || null);
    setPickMealEnd(data.pickMealEnd?.toDate() || null);
    setPaymentType(data.paymentType);
    setRemotePickMealPoint(data.pickMealPoint);
    setCampaignId(data.campaignId || '');
    setGroupHostInfo(data.groupHostInfo || { name: '', note: '' });
  }, []);

  useEffect(() => {
    if (groupId) {
      const unlisten = cartListener(PLATFORM_ID, groupId, handleSnapShot);

      return () => {
        unlisten();
        setOrders([]);
        setCartType(CartType.CART_PERSONAL);
      };
    }
  }, [groupId]);

  return { groupCartStatus: globalDataStatus.cart };
}
